import React, { useState, useEffect, useCallback } from "react";
import Layout from "../layout/Layout";
import { BsDot } from "react-icons/bs";
import { FiAlertCircle, FiUsers } from "react-icons/fi";
import { TbWaveSawTool } from "react-icons/tb";
import ReactPaginate from "react-paginate";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiHandler from "../orthocheck/api";
import LoadingScreen from "../../../../components/custom/LoadingScreen";
import Thanks from "./Thanks";
import Swal from "sweetalert2";

const TreatmentPlan = () => {
  const [page, setPage] = useState(0);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useParams();
  const [showThanksModal, setShowThanksModal] = useState(false);
  const [telehealthLoading, setTelehealthLoading] = useState(false);
  const [showTelehealthButton, setShowTelehealthButton] = useState(true);

  useEffect(() => {
    const hasProviderEnhanced = localStorage.getItem("provider_enhanced");
    const hasProviderInfo = localStorage.getItem("provider_info");

    if (hasProviderEnhanced || hasProviderInfo) {
      setShowTelehealthButton(false);
    }
  }, []);

  const handleTelehealthAppointmentClick = async () => {
    try {
      setTelehealthLoading(true);
      const response = await apiHandler({
        method: "post",
        url: "/ai/telehealth-appointment",
        params: { session },
        raw: true,
        swalError: true,
      });

      if (response?.status === 200) {
        setShowThanksModal(true);
        setTelehealthLoading(false);
        return;
      } else {
        throw new Error("Cannot handle telehealth appointment");
      }
    } catch (error) {
      setTelehealthLoading(false);
      throw new Error("Cannot handle telehealth appointment");
    }
  };

  // Function to fetch treatment plan from API
  const fetchTreatmentPlan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // First try to get data from location.state (for backward compatibility)
      if (location.state?.treatmentPlanData) {
        setResults(location.state.treatmentPlanData);
        setLoading(false);
        return;
      }

      // If no session parameter, show error
      if (!session) {
        throw new Error("No session ID provided");
      }

      const waitTimeSeconds = parseInt(
        process.env.REACT_APP_TREATMENT_PLAN_POLL_WAIT_SECONDS || "1",
        10
      );
      const waitTimeMs = waitTimeSeconds * 1000;

      let attempts = 0;
      const maxAttempts = 30;
      let treatmentPlanData = null;

      while (attempts < maxAttempts) {
        try {
          const response = await apiHandler({
            method: "get",
            url: "/ai/treatmentplan",
            params: { session },
            raw: true,
            swalError: true,
          });

          if (response?.status === 200) {
            treatmentPlanData = response.data;
            break;
          } else if (response?.status === 204) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, waitTimeMs));
            continue;
          } else {
            throw new Error("Cannot find OrthoPlan");
          }
        } catch (error) {
          if (error.response?.status === 204) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, waitTimeMs));
            continue;
          } else if (error.response?.status === 400) {
            throw new Error("Cannot find OrthoPlan");
          } else {
            throw error;
          }
        }
      }

      if (attempts >= maxAttempts) {
        throw new Error("Timeout waiting for treatment plan");
      }

      setResults(treatmentPlanData);
    } catch (error) {
      console.error("Error fetching treatment plan:", error);
      setError(error.message || "Failed to load treatment plan");
    } finally {
      setLoading(false);
    }
  }, [session, location.state?.treatmentPlanData]);

  // Fetch treatment plan on component mount
  useEffect(() => {
    fetchTreatmentPlan();
  }, [fetchTreatmentPlan]);

  const pagesData = results
    ? [
        {
          heading: "Diagnosis",
          data: [
            {
              name: "Symptoms",
              steps: results.diagnoses?.[0]?.symptoms || [],
            },
          ],
        },
        {
          heading: "Key Features",
          data: [
            {
              name: "Key Features",
              steps: results.key_features || [],
            },
          ],
        },
        {
          heading: "Initial Steps",
          data:
            results.treatment_plan?.intial_steps?.map((step) => ({
              name: step.name,
              steps: step.steps,
            })) || [],
        },
        {
          heading: "Referral Steps",
          data:
            results.treatment_plan?.referral_steps?.map((step) => ({
              name: step.name,
              steps: step.steps,
            })) || [],
        },
        {
          heading: "Surgical Steps",
          data:
            results.treatment_plan?.surgical_steps?.map((step) => ({
              name: step.name,
              steps: step.steps,
            })) || [],
        },
        {
          heading: "Warning Signs",
          data: [
            {
              name: "Warning Signs",
              steps: results.warning_signs || [],
            },
          ],
        },
        {
          heading: "Summary",
          data: [
            {
              name: "Summary",
              steps: results.summary ? [results.summary] : [],
            },
          ],
        },
      ].filter((page) => page.data && page.data.length > 0)
    : [];

  useEffect(() => {
    if (page < 0 || page >= pagesData.length) {
      setPage(0);
    }
  }, [page, pagesData.length]);

  const currentPage = pagesData[page];

  const scrollToTop = () => {
    const container = document.getElementById("landing");
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePageClick = ({ selected }) => {
    setPage(selected);
    scrollToTop();
  };

  const handleStartClick = () => {
    navigate(`/providers`);
  };

  // Progress bar calculation
  const totalSteps = pagesData.length;
  const currentStep = page + 1;
  const percentage = `${Math.round((currentStep / totalSteps) * 100)}%`;

  // Render list items
  const renderList = (items, title, icon, bgColor, iconColor) => (
    <div
      className="plan-card border-0 mb-3"
      style={{ backgroundColor: bgColor, borderRadius: "12px" }}
    >
      <div className="card-body">
        {title && (
          <h6
            className="fw-bold mb-3 d-flex align-items-center"
            style={{ color: iconColor }}
          >
            {icon} {title}
          </h6>
        )}
        <ul className="list-unstyled mb-0">
          {items.map((item, index) => (
            <li
              key={index}
              className="d-flex align-items-center p-3 mb-2 rounded"
              style={{
                backgroundColor: "#fff",
                border: "1px solid #f1f1f1",
                color: "#314158",
                gap: "8px",
                lineHeight: "1.5",
              }}
            >
              <BsDot style={{ color: iconColor, flexShrink: 0 }} size={24} />
              <span style={{ flex: 1 }}>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Define color variations
  const colorVariants = [
    { bgColor: "#F5FDF9", iconColor: "#004F3B" }, // green
    { bgColor: "#fff7f0", iconColor: "#7E2A0C" }, // brown
    { bgColor: "#FCFAFE", iconColor: "#7D49A5" }, // purple
    { bgColor: "#F9FBFF", iconColor: "#3271FC" }, // blue
  ];

  // Icons
  const icons = [
    <FiAlertCircle className="me-2" />,
    <FiUsers className="me-2" />,
    <TbWaveSawTool className="me-2" />,
  ];

  // Show loading screen while fetching data
  if (loading) {
    return (
      <Layout>
        <div className="inter-font">
          <div className="container-fluid">
            <div className="text-center mt-5">
              <LoadingScreen />
              <p className="text-muted mt-3">Loading Treatment Plan...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show error message if there's an error
  if (error) {
    return (
      <Layout>
        <div className="plan-bg inter-font" id="landing">
          <div className="container-fluid plan-page">
            <div className="text-center mt-5">
              <h5 className="text-danger">Error Loading Treatment Plan</h5>
              <p className="text-muted">{error}</p>
              <button className="btn btn-primary" onClick={fetchTreatmentPlan}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="plan-bg inter-font" id="landing">
        <div className="container-fluid plan-page">
          {!results ? (
            <div className="text-center mt-5">
              <h5>No treatment plan data found</h5>
            </div>
          ) : (
            <div className="row">
              {((page === 0 && window.innerWidth <= 767) ||
                window.innerWidth > 767) && (
                <div className="col-md-4 right-panel mt-4 mb-3">
                  <div
                    className="plan-card"
                    style={{ backgroundColor: "#F1F8FE" }}
                  >
                    <div className="card-body">
                      <div
                        className="d-flex align-items-start ps-2"
                        style={{ borderLeft: "4px solid #0071FF" }}
                      >
                        <div>
                          <h5
                            className="card-title text-uppercase mb-0"
                            style={{ fontSize: "0.75rem", color: "#0071FF" }}
                          >
                            Name of Diagnosis
                          </h5>
                          <h4 className="fw-bold mb-0">
                            {results.diagnoses?.[0]?.name}
                          </h4>
                        </div>
                      </div>
                      <p className="mt-3" style={{ color: "#314158" }}>
                        {results.diagnoses?.[0]?.summary || results.summary}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* RIGHT PANEL */}
              <div
                className="col-md-8 left-panel mt-4 mb-3"
                style={{ scrollbarWidth: "none" }}
              >
                {/* Progress Bar */}
                <div
                  className="plan-card mb-3 shadow-sm border-0 rounded-3"
                  style={{ backgroundColor: "#EFF2FF" }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#007bff",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        {currentStep}
                      </div>
                      <div>
                        <h6 className="card-title mb-0 fw-bold">
                          {currentPage.heading}
                        </h6>
                        <small className="text-muted">
                          Step {currentStep} of {totalSteps}
                        </small>
                      </div>
                    </div>
                    <div
                      className="progress"
                      style={{ height: "6px", borderRadius: "5px" }}
                    >
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{ width: percentage }}
                        aria-valuenow={parseInt(percentage)}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <small className="text-muted">{percentage}</small>
                      <small className="text-muted">
                        {totalSteps - currentStep} steps left
                      </small>
                    </div>
                  </div>
                </div>

                {/* Render current page content */}
                {Array.isArray(currentPage.data) &&
                  currentPage.data.map((item, idx) => {
                    const variantIndex = (page + idx) % colorVariants.length;
                    const { bgColor, iconColor } = colorVariants[variantIndex];
                    const icon = icons[variantIndex % icons.length];
                    return renderList(
                      item.steps,
                      item.name,
                      icon,
                      bgColor,
                      iconColor
                    );
                  })}

                {/* Pagination */}
                <div className="d-flex align-items-center pagination-wrapper">
                  {/* Back Button */}
                  {page !== pagesData.length - 1 && (
                    <button
                      className="btn btn-outline-secondary rounded-pill px-3 py-1"
                      disabled={page === 0}
                      onClick={() => {
                        setPage((p) => Math.max(p - 1, 0));
                        scrollToTop();
                      }}
                    >
                      &lt; Back
                    </button>
                  )}
                  {/* ReactPaginate with dots */}
                  {page !== pagesData.length - 1 && (
                    <ReactPaginate
                      breakLabel={null}
                      onPageChange={handlePageClick}
                      pageCount={pagesData.length}
                      forcePage={page}
                      containerClassName="pagination mb-0 d-flex align-items-center"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      activeClassName="active"
                      renderOnZeroPageCount={null}
                      previousLabel={null}
                      nextLabel={null}
                    />
                  )}
                  {/* Next / Start button */}
                  {page === pagesData.length - 1 ? (
                    <div className="d-flex flex-column align-items-center w-100">
                      <div className="d-flex flex-wrap justify-content-center gap-3 w-100">
                        {showTelehealthButton && (
                          <button
                            className="btn btn-outline-primary rounded-pill px-4 py-2"
                            onClick={handleTelehealthAppointmentClick}
                          >
                            {telehealthLoading ? (
                              <div className="d-flex justify-content-center align-items-center">
                                <div
                                  className="spinner-border text-primary me-2"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading
                                  </span>
                                </div>
                                <span>Loading...</span>
                              </div>
                            ) : (
                              "Request Telehealth Appointment"
                            )}
                          </button>
                        )}
                        <button
                          className="btn btn-primary rounded-pill px-4 py-2"
                          style={{ color: "white" }}
                          onClick={handleStartClick}
                        >
                          Find a Provider
                        </button>
                      </div>
                      <button
                        className="btn btn-link mt-3"
                        style={{
                          textDecoration: "none",
                          color: "#999999",
                          fontWeight: "500",
                        }}
                        disabled={page === 0}
                        onClick={() => {
                          setPage((p) => Math.max(p - 1, 0));
                          scrollToTop();
                        }}
                      >
                        &lt; Return to Previous Step
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary rounded-pill px-3 py-1"
                      style={{ color: "white" }}
                      onClick={() => {
                        setPage((p) => Math.min(p + 1, pagesData.length - 1));
                        scrollToTop();
                      }}
                    >
                      Next &gt;
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <Thanks
          show={showThanksModal}
          onClose={() => setShowThanksModal(false)}
        />
      </div>
      <style jsx>{`
  #landing {
    height: 100vh;
    overflow-y: auto;
  }
  .pagination-wrapper {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 10px 15px;
    z-index: 1000;
    width: 100%;
    box-sizing: border-box;
  }
  .pagination {
    gap: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
  }
  .pagination .page-link {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    padding: 0;
    margin: 0;
    border: none;
    background-color: #e5e7eb;
    font-size: 0;
    transition: background-color 0.2s;
  }
  .pagination .active .page-link {
    background-color: #007bff;
  }
  @media (min-width: 577px) {
    .pagination-wrapper {
      padding: 15px 20px;
    }
    .pagination {
      gap: 8px;
    }
    .pagination .page-link {
      width: 10px;
      height: 10px;
    }
    .btn {
      padding: 8px 16px;
    }
  }
  @media (max-width: 576px) {
    .pagination-wrapper {
      padding: 12px 10px;
    }
    .pagination {
      gap: 6px;
      flex-grow: 1;
      justify-content: center;
      margin: 0 10px;
    }
    .btn {
      padding: 6px 12px;
      font-size: 14px;
      min-width: 60px;
    }
    .pagination .page-link {
      width: 8px;
      height: 8px;
    }
  }
  @media (max-width: 767px) {
   @media (max-width: 767px) {
  #landing {
    height: auto;
    overflow-y: visible;
  }
  .pagination {
    justify-content: flex-start;
  }
  .pagination .page-link {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 0;
    min-width: auto;
  }

  }
`}</style>
    </Layout>
  );
};

export default TreatmentPlan;
